import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Vehicle {
  id: string;
  name: string;
  image_url: string | null;
  capacity: number;
  description: string | null;
  features: string[] | null;
}

const FleetSection = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    supabase
      .from("vehicles")
      .select("id, name, image_url, capacity, description, features")
      .eq("is_active", true)
      .order("sort_order")
      .limit(4)
      .then(({ data }) => {
        if (data) setVehicles(data);
      });
  }, []);

  if (vehicles.length === 0) return null;

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="text-secondary font-semibold text-sm uppercase tracking-widest">Our Fleet</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mt-2">
            Travel in Comfort & Style
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            Modern, well-maintained vehicles for every group size
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {vehicles.map((v, i) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white border border-border/50 rounded-none overflow-hidden hover:shadow-lg transition-shadow group"
            >
              <div className="h-44 overflow-hidden">
                <img
                  src={v.image_url || "/placeholder.svg"}
                  alt={v.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-foreground mb-1">{v.name}</h3>
                <div className="flex items-center gap-1 text-muted-foreground text-sm mb-2">
                  <Users className="w-4 h-4" />
                  Up to {v.capacity} passengers
                </div>
                {v.features && v.features.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {v.features.slice(0, 3).map((f) => (
                      <span key={f} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-none">
                        {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/fleet"
            className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all"
          >
            View All Vehicles <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
